import { LoadingState } from '@/entities/LoadingState';
import { MutationTree, ActionTree, GetterTree, Module, ActionContext } from 'vuex';
import { RootState } from '../rootState';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { ShoppingCart } from '@/entities/ShoppingCart';
import { NotificationModuleTypes, NotificationType } from './notification';
import { ResHelper } from '@/helper/ResHelper';
import * as l10n from '@/constants/l10n';
import removeNamespace from '../removeNamespace';
import { PartAvailability } from '@/entities/PartAvailability';
import { PartService } from '@/services/part-service';
import Vue from 'vue';
import { UserHelper } from '@/helper/user';
import { ExactPartSearchResultInfo } from '@/entities/ExactPartSearchResultInfo';
import { PartSearchService } from '@/services/part-search-service';
import { ShippingMethod } from '@/entities/ShippingMethod';
import { OrderMethod } from '@/entities/OrderMethod';
import { PartAnalytics } from '@/entities/analytics/PartAnalytics';
import { trackAddItemsToShoppingCart } from '@/analytics';
import { AnalyticsService } from '@/services/analytics-service';
import _ from 'lodash';

const shoppingCartService = new ShoppingCartService();
const partService = new PartService();
const searchService = new PartSearchService();
const analyticsService = new AnalyticsService();

const namespace = 'shoppingCart';

export const ShoppingCartModuleTypes = {
    getters: {
        loadingState: `${namespace}/loadingState`,
        shoppingCarts: `${namespace}/shoppingCarts`,
        activeShoppingCart: `${namespace}/activeShoppingCart`,
        itemsThatAreBeingAddded: `${namespace}/itemsThatAreBeingAddded`,
        isItemCurrentlyBeingAdded: `${namespace}/isItemCurrentlyBeingAdded`,
        hasActiveShoppingCart: `${namespace}/hasActiveShoppingCart`,
        isItemLoadingAvailability: `${namespace}/isItemLoadingAvailability`,
        partSearchState: `${namespace}/partSearchState`,
        foundPartsByDirectInput: `${namespace}/foundPartsByDirectInput`,
        orderMethods: `${namespace}/orderMethods`,
    },
    mutations: {
        loadShoppingCarts: `${namespace}/loadShoppingCarts`,
        loadShoppingCartsSuccess: `${namespace}/loadShoppingCartsSuccess`,
        loadShoppingCartsFail: `${namespace}/loadShoppingCartsFail`,
        setActiveSelectedShoppingCart: `${namespace}/setActiveSelectedShoppingCart`,
        addShoppingCart: `${namespace}/addShoppingCart`,
        addShoppingCartSuccess: `${namespace}/addShoppingCartSuccess`,
        addShoppingCartFail: `${namespace}/addShoppingCartFail`,
        deleteActiveShoppingCart: `${namespace}/deleteActiveShoppingCart`,
        deleteActiveShoppingCartSuccess: `${namespace}/deleteActiveShoppingCartSuccess`,
        deleteActiveShoppingCartFail: `${namespace}/deleteActiveShoppingCartFail`,
        addShoppingCartItem: `${namespace}/addShoppingCartItem`,
        addShoppingCartItemSuccess: `${namespace}/addShoppingCartItemSuccess`,
        addShoppingCartItemsInBulk: `${namespace}/addShoppingCartItemsInBulk`,
        updateActiveShoppingCartMetadata: `${namespace}/updateActiveShoppingCartMetadata`,
        updateActiveShoppingCartMetadataSuccess: `${namespace}/updateActiveShoppingCartMetadataSuccess`,
        updateActiveShoppingCartMetadataFail: `${namespace}/updateActiveShoppingCartMetadataFail`,
        updateActiveShoppingCartShippingMethod: `${namespace}/updateActiveShoppingCartShippingMethod`,
        updateActiveShoppingCartShippingMethodSuccess: `${namespace}/updateActiveShoppingCartShippingMethodSuccess`,
        updateActiveShoppingCartShippingMethodFail: `${namespace}/updateActiveShoppingCartShippingMethodFail`,
        updateActiveShoppingCartOrderMethod: `${namespace}/updateActiveShoppingCartOrderMethod`,
        updateActiveShoppingCartOrderMethodSuccess: `${namespace}/updateActiveShoppingCartOrderMethodSuccess`,
        updateActiveShoppingCartOrderMethodFail: `${namespace}/updateActiveShoppingCartOrderMethodFail`,
        updateShoppingCartOrderMethod: `${namespace}/updateShoppingCartOrderMethod`,
        updateQuantityOfShoppingCartItem: `${namespace}/updateQuantityOfShoppingCartItem`,
        updateQuantityOfShoppingCartItemSuccess: `${namespace}/updateQuantityOfShoppingCartItemSuccess`,
        updateQuantityOfShoppingCartItemFail: `${namespace}/updateQuantityOfShoppingCartItemFail`,
        updateUserInfoTextOfShoppingCartItem: `${namespace}/updateUserInfoTextOfShoppingCartItem`,
        updateUserInfoTextOfShoppingCartItemSuccess: `${namespace}/updateUserInfoTextOfShoppingCartItemSuccess`,
        updateUserInfoTextOfShoppingCartItemFail: `${namespace}/updateUserInfoTextOfShoppingCartItemFail`,
        deleteItemFromActiveShoppingCart: `${namespace}/deleteItemFromActiveShoppingCart`,
        deleteItemFromActiveShoppingCartSuccess: `${namespace}/deleteItemFromActiveShoppingCartSuccess`,
        deleteItemFromActiveShoppingCartFail: `${namespace}/deleteItemFromActiveShoppingCartFail`,
        moveItemToShoppingCart: `${namespace}/moveItemToShoppingCart`,
        moveItemToShoppingCartSuccess: `${namespace}/moveItemToShoppingCartSuccess`,
        moveItemToShoppingCartFail: `${namespace}/moveItemToShoppingCartFail`,
        reloadAvailabilityOfShoppingCartItem: `${namespace}/reloadAvailabilityOfShoppingCartItem`,
        reloadAvailabilityOfShoppingCartItemSuccess: `${namespace}/reloadAvailabilityOfShoppingCartItemSuccess`,
        reloadAvailabilityOfShoppingCartItemFail: `${namespace}/reloadAvailabilityOfShoppingCartItemFail`,
        searchForPartsByPartNumber: `${namespace}/searchForPartsByPartNumber`,
        searchForPartsByPartNumberSuccess: `${namespace}/searchForPartsByPartNumberSuccess`,
        searchForPartsByPartNumberFail: `${namespace}/searchForPartsByPartNumberFail`,
        clearFoundPartsByDirectInput: `${namespace}/clearFoundPartsByDirectInput`,
    },
    actions: {
        loadShoppingCarts: `${namespace}/loadShoppingCarts`,
        setActiveShoppingCart: `${namespace}/setActiveShoppingCart`,
        addShoppingCart: `${namespace}/addShoppingCart`,
        deleteActiveShoppingCart: `${namespace}/deleteActiveShoppingCart`,
        addShoppingCartItem: `${namespace}/addShoppingCartItem`,
        addShoppingCartItemsInBulk: `${namespace}/addShoppingCartItemsInBulk`,
        updateActiveShoppingCartMetadata: `${namespace}/updateActiveShoppingCartMetadata`,
        updateActiveShoppingCartShippingMethod: `${namespace}/updateActiveShoppingCartShippingMethod`,
        updateActiveShoppingCartOrderMethod: `${namespace}/updateActiveShoppingCartOrderMethod`,
        updateQuantityOfShoppingCartItem: `${namespace}/updateQuantityOfShoppingCartItem`,
        reloadAvailabilityOfShoppingCartItem: `${namespace}/reloadAvailabilityOfShoppingCartItem`,
        updateUserInfoTextOfShoppingCartItem: `${namespace}/updateUserInfoTextOfShoppingCartItem`,
        deleteItemFromActiveShoppingCart: `${namespace}/deleteItemFromActiveShoppingCart`,
        moveItemToShoppingCart: `${namespace}/moveItemToShoppingCart`,
        searchForPartsByPartNumber: `${namespace}/searchForPartsByPartNumber`,
        clearFoundPartsByDirectInput: `${namespace}/clearFoundPartsByDirectInput`,
    },
};

const types = removeNamespace(namespace, ShoppingCartModuleTypes);
export interface ShoppingCartState {
    activeShoppingCart?: ShoppingCart;
    shoppingCarts: ShoppingCart[];
    loadingState: LoadingState;
    itemsThatAreBeingAdded: { [key: string]: boolean };
    itemsThatAreLoadingAvailability: { [key: string]: boolean };
    partSearchState: LoadingState;
    foundPartsByDirectInput: ExactPartSearchResultInfo[];
    orderMethods: OrderMethod[];
}

const state: ShoppingCartState = {
    activeShoppingCart: undefined,
    shoppingCarts: [],
    loadingState: LoadingState.Indeterminate,
    itemsThatAreBeingAdded: {},
    itemsThatAreLoadingAvailability: {},
    partSearchState: LoadingState.Indeterminate,
    foundPartsByDirectInput: [],
    orderMethods: [],
};

const mutations: MutationTree<ShoppingCartState> = {
    [types.mutations.loadShoppingCarts](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
        currentState.shoppingCarts = [];
        currentState.orderMethods = [];
        currentState.activeShoppingCart = undefined;
    },
    [types.mutations.loadShoppingCartsSuccess](
        currentState: ShoppingCartState,
        payload: { carts: ShoppingCart[]; orderMethods: OrderMethod[] }
    ): void {
        let currentCart = payload.carts.find(
            (cart) => currentState.activeShoppingCart != null && cart.id === currentState.activeShoppingCart.id
        );
        if (!currentCart) {
            currentCart = payload.carts.find((cart) => cart.isActiveCart);
        }
        currentState.activeShoppingCart = currentCart!;
        currentState.shoppingCarts = payload.carts;
        currentState.orderMethods = payload.orderMethods;
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.loadShoppingCartsFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.setActiveSelectedShoppingCart](
        currentState: ShoppingCartState,
        payload: { shoppingCartId: number }
    ): void {
        const shoppingCart = currentState.shoppingCarts.find((cart) => cart.id === payload.shoppingCartId);
        if (shoppingCart) {
            currentState.activeShoppingCart = shoppingCart;
        }
    },
    [types.mutations.deleteActiveShoppingCart](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.deleteActiveShoppingCartSuccess](
        currentState: ShoppingCartState,
        payload: { shoppingCartId: number }
    ): void {
        if (currentState.shoppingCarts) {
            currentState.shoppingCarts = currentState.shoppingCarts.filter(
                (cart) => cart.id !== payload.shoppingCartId
            );
            currentState.activeShoppingCart = currentState.shoppingCarts[0];
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.deleteActiveShoppingCartFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.moveItemToShoppingCart](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.moveItemToShoppingCartSuccess](
        currentState: ShoppingCartState,
        payload: { shoppingCartItemId: number; targetShoppingCartId: number }
    ): void {
        if (!currentState.shoppingCarts || !currentState.activeShoppingCart) {
            return;
        }
        const foundItem = currentState.activeShoppingCart.cartItems.find(
            (item) => item.id === payload.shoppingCartItemId
        );
        const targetCart = currentState.shoppingCarts.find((cart) => cart.id === payload.targetShoppingCartId);
        if (!foundItem || !targetCart) {
            return;
        }
        targetCart.cartItems.push(foundItem);
        currentState.activeShoppingCart.cartItems = currentState.activeShoppingCart.cartItems.filter(
            (item) => item.id !== payload.shoppingCartItemId
        );
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.moveItemToShoppingCartFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.addShoppingCart](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.addShoppingCartSuccess](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.addShoppingCartFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.addShoppingCartItem](
        currentState: ShoppingCartState,
        payload: { fullPartId: string; quantity: number }
    ): void {
        currentState.itemsThatAreBeingAdded = {
            ...currentState.itemsThatAreBeingAdded,
            [payload.fullPartId]: true,
        };
    },
    [types.mutations.addShoppingCartItemSuccess](
        currentState: ShoppingCartState,
        payload: { fullPartId: string; quantity: number }
    ): void {
        currentState.itemsThatAreBeingAdded = {
            ...currentState.itemsThatAreBeingAdded,
            [payload.fullPartId]: false,
        };
    },
    [types.mutations.updateActiveShoppingCartMetadata](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.updateActiveShoppingCartMetadataSuccess](
        currentState: ShoppingCartState,
        payload: { userId: string; cartName: string; orderName: string }
    ): void {
        if (currentState.activeShoppingCart) {
            currentState.activeShoppingCart.cartName = payload.cartName;
            currentState.activeShoppingCart.orderName = payload.orderName;
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.updateActiveShoppingCartMetadataFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.updateActiveShoppingCartShippingMethod](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.updateActiveShoppingCartShippingMethodSuccess](
        currentState: ShoppingCartState,
        payload: { shippingMethod: ShippingMethod }
    ): void {
        if (currentState.activeShoppingCart) {
            currentState.activeShoppingCart.shippingMethod = payload.shippingMethod;
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.updateActiveShoppingCartShippingMethodFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.updateActiveShoppingCartOrderMethod](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.updateActiveShoppingCartOrderMethodSuccess](
        currentState: ShoppingCartState,
        payload: { orderMethod: string }
    ): void {
        if (currentState.activeShoppingCart) {
            currentState.activeShoppingCart.orderMethod = payload.orderMethod;
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.updateShoppingCartOrderMethod](
        currentState: ShoppingCartState,
        payload: { shoppingCartId: number; orderMethod: string }
    ): void {
        const shoppingCart = currentState.shoppingCarts.find((cart) => cart.id === payload.shoppingCartId);
        if (shoppingCart) {
            shoppingCart.orderMethod = payload.orderMethod;
        }
    },
    [types.mutations.updateActiveShoppingCartOrderMethodFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.updateQuantityOfShoppingCartItem](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.updateQuantityOfShoppingCartItemSuccess](
        currentState: ShoppingCartState,
        payload: {
            userId: string;
            shoppingCartItemId: number;
            quantity: number;
        }
    ): void {
        if (currentState.activeShoppingCart) {
            const cartItem = currentState.activeShoppingCart.cartItems.find(
                (item) => item.id === payload.shoppingCartItemId
            );
            if (cartItem) {
                cartItem.quantity = payload.quantity;
            }
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.updateQuantityOfShoppingCartItemFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.updateUserInfoTextOfShoppingCartItem](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.updateUserInfoTextOfShoppingCartItemSuccess](
        currentState: ShoppingCartState,
        payload: {
            userId: string;
            shoppingCartItemId: number;
            userInfoText: string;
        }
    ): void {
        if (currentState.activeShoppingCart) {
            const cartItem = currentState.activeShoppingCart.cartItems.find(
                (item) => item.id === payload.shoppingCartItemId
            );
            if (cartItem) {
                cartItem.userInfoText = payload.userInfoText;
            }
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.updateUserInfoTextOfShoppingCartItemFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },

    [types.mutations.deleteItemFromActiveShoppingCart](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Loading;
    },
    [types.mutations.deleteItemFromActiveShoppingCartSuccess](
        currentState: ShoppingCartState,
        payload: { userId: string; shoppingCartItemId: number }
    ): void {
        if (currentState.activeShoppingCart) {
            currentState.activeShoppingCart.cartItems = currentState.activeShoppingCart.cartItems.filter(
                (item) => item.id !== payload.shoppingCartItemId
            );
        }
        currentState.loadingState = LoadingState.Loaded;
    },
    [types.mutations.deleteItemFromActiveShoppingCartFail](currentState: ShoppingCartState): void {
        currentState.loadingState = LoadingState.Failed;
    },
    [types.mutations.reloadAvailabilityOfShoppingCartItem](
        currentState: ShoppingCartState,
        payload: { shoppingCartItemId: number }
    ): void {
        currentState.itemsThatAreLoadingAvailability = {
            ...currentState.itemsThatAreLoadingAvailability,
            [payload.shoppingCartItemId]: true,
        };
    },
    [types.mutations.reloadAvailabilityOfShoppingCartItemSuccess](
        currentState: ShoppingCartState,
        payload: { shoppingCartItemId: number; availability: PartAvailability }
    ): void {
        currentState.itemsThatAreLoadingAvailability = {
            ...currentState.itemsThatAreLoadingAvailability,
            [payload.shoppingCartItemId]: false,
        };
        if (!currentState.activeShoppingCart) {
            return;
        }
        const itemIndex = currentState.activeShoppingCart.cartItems.findIndex(
            (item) => item.id === payload.shoppingCartItemId
        );
        Vue.set(currentState.activeShoppingCart.cartItems, itemIndex, {
            ...currentState.activeShoppingCart.cartItems[itemIndex],
            partAvailability: payload.availability,
        });
    },
    [types.mutations.reloadAvailabilityOfShoppingCartItemFail](
        currentState: ShoppingCartState,
        payload: { shoppingCartItemId: number }
    ): void {
        currentState.itemsThatAreLoadingAvailability = {
            ...currentState.itemsThatAreLoadingAvailability,
            [payload.shoppingCartItemId]: false,
        };
    },
    [types.mutations.searchForPartsByPartNumber](currentState: ShoppingCartState): void {
        currentState.partSearchState = LoadingState.Loading;
    },
    [types.mutations.searchForPartsByPartNumberSuccess](
        currentState: ShoppingCartState,
        payload: { parts: ExactPartSearchResultInfo[] }
    ): void {
        currentState.foundPartsByDirectInput = payload.parts;
        currentState.partSearchState = LoadingState.Loaded;
    },
    [types.mutations.searchForPartsByPartNumberFail](currentState: ShoppingCartState): void {
        currentState.foundPartsByDirectInput = [];
        currentState.partSearchState = LoadingState.Failed;
    },
    [types.mutations.clearFoundPartsByDirectInput](currentState: ShoppingCartState): void {
        currentState.foundPartsByDirectInput = [];
    },
};

const actions: ActionTree<ShoppingCartState, RootState> = {
    async [types.actions.loadShoppingCarts](context: ActionContext<ShoppingCartState, RootState>): Promise<void> {
        context.commit(types.mutations.loadShoppingCarts);
        try {
            const carts = await shoppingCartService.getShoppingCarts();
            const orderMethods = await shoppingCartService.getOrderMethods();
            if (!UserHelper.hasShippingCosts()) {
                // For customers who don't have shipping costs, we have to check if order methods are set and valid.
                // Otherwise initialize with first value of order methods.
                for (const cart of carts) {
                    const isOrderMethodValid =
                        orderMethods.find((method) => method.value === cart.orderMethod) !== undefined;
                    if (!isOrderMethodValid) {
                        const firstOrderMethod = orderMethods[0].value;
                        await shoppingCartService.updateShoppingCartOrderMethod(cart.id, firstOrderMethod);
                        context.commit(types.mutations.updateShoppingCartOrderMethod, {
                            shoppingCartId: cart.id,
                            orderMethod: firstOrderMethod,
                        });
                    }
                }
            }
            context.commit(types.mutations.loadShoppingCartsSuccess, {
                carts,
                orderMethods,
            });
        } catch (error) {
            context.commit(types.mutations.loadShoppingCartsFail);
        }
    },
    async [types.actions.setActiveShoppingCart](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartId: number }
    ): Promise<void> {
        if (context.state.shoppingCarts.length > 0) {
            context.commit(types.mutations.setActiveSelectedShoppingCart, payload);
            if (UserHelper.isAuthenticated()) {
                await shoppingCartService.setActiveShoppingCart(payload.shoppingCartId);
            }
        }
    },
    async [types.actions.deleteActiveShoppingCart](
        context: ActionContext<ShoppingCartState, RootState>
    ): Promise<void> {
        if (!context.state.activeShoppingCart || context.state.shoppingCarts.length <= 1) {
            return;
        }
        const cartIdToDelete = context.state.activeShoppingCart.id;
        const cartNameToDelete = context.state.activeShoppingCart.cartName;
        try {
            context.commit(types.mutations.deleteActiveShoppingCart);
            await shoppingCartService.deleteShoppingCart(cartIdToDelete);
            context.commit(types.mutations.deleteActiveShoppingCartSuccess, {
                shoppingCartId: cartIdToDelete,
            });
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(
                        l10n.SHOPPING_CART.DELETE_CART_SUCCESS,
                        cartNameToDelete
                    ),
                    type: NotificationType.Success,
                },
                { root: true }
            );
        } catch (error) {
            context.commit(types.mutations.deleteActiveShoppingCartFail);
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(l10n.SHOPPING_CART.DELETE_CART_FAILED, cartNameToDelete),
                    type: NotificationType.Error,
                },
                { root: true }
            );
        }
    },
    async [types.actions.addShoppingCart](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { cartName: string }
    ): Promise<void> {
        context.commit(types.mutations.addShoppingCart);
        let shoppingCartId;
        try {
            shoppingCartId = await shoppingCartService.addShoppingCart(payload.cartName);
            context.commit(types.mutations.addShoppingCartSuccess);
        } catch (error) {
            context.commit(types.mutations.addShoppingCartFail);
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getString(l10n.SHOPPING_CART.CREATE_NEW_CART_FAILED),
                    type: NotificationType.Error,
                },
                { root: true }
            );
            return;
        }
        await context.dispatch(types.actions.loadShoppingCarts);
        await context.dispatch(types.actions.setActiveShoppingCart, {
            shoppingCartId,
        });
        context.dispatch(
            NotificationModuleTypes.actions.notify,
            {
                message: ResHelper.getString(l10n.SHOPPING_CART.CREATE_NEW_CART_SUCCESS),
                type: NotificationType.Success,
            },
            { root: true }
        );
    },
    async [types.actions.addShoppingCartItem](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: {
            fullPartId: string;
            partNumber: string;
            quantity: number;
        }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.addShoppingCartItem, payload);
        try {
            await shoppingCartService.addShoppingCartItem(
                context.state.activeShoppingCart.id,
                payload.fullPartId,
                payload.quantity
            );
            context.commit(types.mutations.addShoppingCartItemSuccess, payload);
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(l10n.SHOPPING_CART.ADD_SUCCESS, payload.partNumber),
                    type: NotificationType.Success,
                },
                { root: true }
            );
            context.dispatch(types.actions.loadShoppingCarts);
            analyticsService
                .getShoppingCartItemAnalytics(payload.fullPartId, payload.quantity)
                .then((shoppingCartItemAnalytics) => {
                    if (shoppingCartItemAnalytics == null) {
                        return;
                    }
                    trackAddItemsToShoppingCart([shoppingCartItemAnalytics]);
                });
        } catch (error) {
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(l10n.SHOPPING_CART.ADD_FAILED, payload.partNumber),
                    type: NotificationType.Error,
                },
                { root: true }
            );
            context.dispatch(types.actions.loadShoppingCarts);
        }
    },
    async [types.actions.addShoppingCartItemsInBulk](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { items: Array<{ partId: string; quantity: number; userInfoText: string }>; cartId?: number }
    ): Promise<void> {
        try {
            if (payload.cartId === undefined) {
                // create new cart first
                let i = 1;
                let availableCartName;
                const cartNames = context.state.shoppingCarts.map((cart) => cart.cartName);
                while (availableCartName === undefined) {
                    const cartNameToTry = `Cart ${i}`;
                    if (!cartNames.includes(cartNameToTry)) {
                        availableCartName = cartNameToTry;
                        break;
                    }
                    i++;
                }

                await context.dispatch(types.actions.addShoppingCart, { cartName: availableCartName });
                payload.cartId = context.state.activeShoppingCart!.id;
            }

            const foundCart = context.state.shoppingCarts.find((cart) => cart.id === payload.cartId);
            if (!foundCart) {
                return;
            }

            const cartItems = payload.items.map((item) => {
                return { fullPartId: item.partId, quantity: item.quantity };
            });
            const chunkedCartItems = _.chunk(cartItems, 100);
            const promises = chunkedCartItems.map((items) =>
                shoppingCartService.addShoppingCartItemsBulk(foundCart.id, items)
            );
            await Promise.all(promises);

            await context.dispatch(types.actions.loadShoppingCarts);

            payload.items.map((item) => {
                const cartItem = context.state.activeShoppingCart!.cartItems.find(
                    (ci) => ci.fullPartId === item.partId
                );
                if (cartItem && item.userInfoText) {
                    shoppingCartService.updateUserInfoTextOfShoppingCartItem(
                        foundCart.id,
                        cartItem.id,
                        item.userInfoText
                    );
                }
            });

            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getString(l10n.SHOPPING_CART.ADD_BULK_SUCCESS),
                    type: NotificationType.Success,
                },
                { root: true }
            );

            analyticsService.getMultipleShoppingCartItemAnalytics(payload.items).then(trackAddItemsToShoppingCart);
        } catch (error) {
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getString(l10n.SHOPPING_CART.ADD_BULK_FAILED),
                    type: NotificationType.Error,
                },
                { root: true }
            );
        }
    },
    async [types.actions.updateActiveShoppingCartMetadata](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { cartName: string; orderName: string }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.updateActiveShoppingCartMetadata);
        try {
            await shoppingCartService.updateShoppingCartMetadata(
                context.state.activeShoppingCart.id,
                payload.cartName,
                payload.orderName
            );
            context.commit(types.mutations.updateActiveShoppingCartMetadataSuccess, payload);
        } catch (error) {
            context.commit(types.mutations.updateActiveShoppingCartMetadataFail);
        }
    },
    async [types.actions.updateActiveShoppingCartShippingMethod](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shippingMethod: ShippingMethod }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.updateActiveShoppingCartShippingMethod);
        try {
            await shoppingCartService.updateShoppingCartShippingMethod(
                context.state.activeShoppingCart.id,
                payload.shippingMethod
            );
            context.commit(types.mutations.updateActiveShoppingCartShippingMethodSuccess, payload);
        } catch (error) {
            context.commit(types.mutations.updateActiveShoppingCartShippingMethodFail);
        }
    },
    async [types.actions.updateActiveShoppingCartOrderMethod](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { orderMethod: string }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.updateActiveShoppingCartOrderMethod);
        try {
            await shoppingCartService.updateShoppingCartOrderMethod(
                context.state.activeShoppingCart.id,
                payload.orderMethod
            );
            context.commit(types.mutations.updateActiveShoppingCartOrderMethodSuccess, payload);
            // change of order method can change item prices, therefore reload shopping carts
            context.dispatch(types.actions.loadShoppingCarts);
        } catch (error) {
            context.commit(types.mutations.updateActiveShoppingCartOrderMethodFail);
        }
    },
    async [types.actions.updateQuantityOfShoppingCartItem](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartItemId: number; quantity: number }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.updateQuantityOfShoppingCartItem);
        try {
            await shoppingCartService.updateQuantityOfShoppingCartItem(
                context.state.activeShoppingCart.id,
                payload.shoppingCartItemId,
                payload.quantity
            );
            context.commit(types.mutations.updateQuantityOfShoppingCartItemSuccess, payload);
            context.dispatch(types.actions.reloadAvailabilityOfShoppingCartItem, {
                shoppingCartItemId: payload.shoppingCartItemId,
            });
        } catch (e) {
            context.commit(types.mutations.updateQuantityOfShoppingCartItemFail);
        }
    },
    async [types.actions.updateUserInfoTextOfShoppingCartItem](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartItemId: number; userInfoText: string }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.updateUserInfoTextOfShoppingCartItem);
        try {
            await shoppingCartService.updateUserInfoTextOfShoppingCartItem(
                context.state.activeShoppingCart.id,
                payload.shoppingCartItemId,
                payload.userInfoText
            );
            context.commit(types.mutations.updateUserInfoTextOfShoppingCartItemSuccess, payload);
        } catch (e) {
            context.commit(types.mutations.updateUserInfoTextOfShoppingCartItemFail);
        }
    },
    async [types.actions.deleteItemFromActiveShoppingCart](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartItemId: number }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }
        context.commit(types.mutations.deleteItemFromActiveShoppingCart);
        try {
            await shoppingCartService.deleteShoppingCartItem(
                context.state.activeShoppingCart.id,
                payload.shoppingCartItemId
            );
            context.commit(types.mutations.deleteItemFromActiveShoppingCartSuccess, payload);
        } catch (e) {
            context.commit(types.mutations.deleteItemFromActiveShoppingCartFail);
        }
    },
    async [types.actions.moveItemToShoppingCart](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartItemId: number; targetShoppingCartId: number }
    ): Promise<void> {
        const targetCart = context.state.shoppingCarts.find((cart) => cart.id === payload.targetShoppingCartId);
        if (!context.state.activeShoppingCart || !targetCart) {
            return;
        }

        context.commit(types.mutations.moveItemToShoppingCart);
        try {
            await shoppingCartService.moveItemToShoppingCart(
                context.state.activeShoppingCart.id,
                payload.shoppingCartItemId,
                payload.targetShoppingCartId
            );
            context.commit(types.mutations.moveItemToShoppingCartSuccess, payload);
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(
                        l10n.SHOPPING_CART.MOVE_ITEM_SUCCESS,
                        targetCart.cartName
                    ),
                    type: NotificationType.Success,
                },
                { root: true }
            );
        } catch (error) {
            context.commit(types.mutations.moveItemToShoppingCartFail);
            context.dispatch(
                NotificationModuleTypes.actions.notify,
                {
                    message: ResHelper.getStringWithParameters(
                        l10n.SHOPPING_CART.MOVE_ITEM_FAILED,
                        targetCart.cartName
                    ),
                    type: NotificationType.Error,
                },
                { root: true }
            );
        }
    },
    async [types.actions.reloadAvailabilityOfShoppingCartItem](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { shoppingCartItemId: number }
    ): Promise<void> {
        if (!context.state.activeShoppingCart) {
            return;
        }

        const cartItem = context.state.activeShoppingCart.cartItems.find(
            (item) => item.id === payload.shoppingCartItemId
        );
        if (!cartItem) {
            return;
        }
        let orderMethod;
        if (!UserHelper.hasShippingCosts()) {
            orderMethod = context.state.activeShoppingCart.orderMethod;
        }

        context.commit(types.mutations.reloadAvailabilityOfShoppingCartItem, {
            shoppingCartItemId: payload.shoppingCartItemId,
        });
        try {
            const availability = await partService.getAvailabilityOfPart(
                cartItem.fullPartId,
                cartItem.quantity,
                orderMethod
            );
            context.commit(types.mutations.reloadAvailabilityOfShoppingCartItemSuccess, {
                shoppingCartItemId: payload.shoppingCartItemId,
                availability,
            });
        } catch (error) {
            context.commit(types.mutations.reloadAvailabilityOfShoppingCartItemFail, {
                shoppingCartItemId: payload.shoppingCartItemId,
            });
        }
        return;
    },
    async [types.actions.searchForPartsByPartNumber](
        context: ActionContext<ShoppingCartState, RootState>,
        payload: { partNumber: string }
    ): Promise<void> {
        context.commit(types.mutations.searchForPartsByPartNumber);
        try {
            const partSearchResult = await searchService.searchPartsByPartNumber(payload.partNumber);
            context.commit(types.mutations.searchForPartsByPartNumberSuccess, {
                parts: partSearchResult.resultInfos,
            });
        } catch (error) {
            context.commit(types.mutations.searchForPartsByPartNumberFail);
        }
    },
    [types.actions.clearFoundPartsByDirectInput](context: ActionContext<ShoppingCartState, RootState>): void {
        context.commit(types.mutations.clearFoundPartsByDirectInput);
    },
};

const getters: GetterTree<ShoppingCartState, RootState> = {
    [types.getters.loadingState]: (currentState) => currentState.loadingState,
    [types.getters.shoppingCarts]: (currentState) => currentState.shoppingCarts,
    [types.getters.activeShoppingCart]: (currentState) => currentState.activeShoppingCart,
    [types.getters.itemsThatAreBeingAddded](currentState: ShoppingCartState): { [key: string]: boolean } {
        return currentState.itemsThatAreBeingAdded;
    },
    [types.getters.isItemCurrentlyBeingAdded](currentState: ShoppingCartState): (fullPartId: string) => boolean {
        return (fullPartId: string) => currentState.itemsThatAreBeingAdded[fullPartId];
    },
    [types.getters.hasActiveShoppingCart](currentState: ShoppingCartState): boolean {
        return currentState.activeShoppingCart != null;
    },
    [types.getters.isItemLoadingAvailability](
        currentState: ShoppingCartState
    ): (shoppingCartItemId: number) => boolean {
        return (shoppingCartItemId: number) => currentState.itemsThatAreLoadingAvailability[shoppingCartItemId];
    },
    [types.getters.partSearchState]: (currentState) => currentState.partSearchState,
    [types.getters.foundPartsByDirectInput]: (currentState) => currentState.foundPartsByDirectInput,
    [types.getters.orderMethods]: (currentState) => currentState.orderMethods,
};

const module: Module<ShoppingCartState, RootState> = {
    state,
    mutations,
    actions,
    getters,
    namespaced: true,
};

export default module;
